import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Package, MapPin, Truck, User, Phone, Mail, Calendar, RefreshCw, Edit } from 'lucide-react';
import { getStatusLabel, getStatusVariant, type OrderStatus } from '@/lib/statusMapping';
import { toast } from 'sonner';
import OrderEditDialog from './OrderEditDialog';

interface OrderDetailModalProps {
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOrderUpdated: () => void;
}

interface OrderDetails {
  id: string;
  order_number: string;
  total_amount: number;
  total_delivery_cost: number;
  status: string;
  created_at: string;
  user_id: string;
  shipping_city?: string | null;
  shipping_option?: string | null;
  billing_address?: any;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    phone: string | null;
    tax_id: string | null;
    business_name: string | null;
    business_address: string | null;
  };
  sub_orders: Array<{
    id: string;
    order_number: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    delivery_cost: number;
    delivery_option: string;
    notes: string | null;
    pickup_address: string | null;
    delivery_address: string | null;
    products: {
      name: string;
      description: string;
      measurement_unit: string;
      package_coverage_per_unit?: number;
      items_per_package?: number;
      images?: string[];
      main_image_index?: number;
    };
  }>;
}

const OrderDetailModal = ({ orderId, isOpen, onClose, onOrderUpdated }: OrderDetailModalProps) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  useEffect(() => {
    if (orderId && isOpen) {
      fetchOrderDetails();
    }
  }, [orderId, isOpen]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      // Fetch order with profile - specify columns explicitly to avoid ambiguity
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select(`
          id, user_id, status, total_amount, total_delivery_cost,
          order_number, created_at, updated_at, billing_address,
          shipping_city, shipping_option
        `)
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      // Fetch user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, phone, tax_id, business_name, business_address')
        .eq('id', order.user_id)
        .single();

      // Fetch sub-orders with products data
      const { data: subOrders, error: subOrdersError } = await supabase
        .from('sub_orders')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at');

      if (subOrdersError) throw subOrdersError;

      // Fetch product images for sub-orders
      const productIds = (subOrders || []).map(so => so.product_id).filter(Boolean);
      let productsMap = new Map();

      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, images, main_image_index, measurement_unit, package_coverage_per_unit')
          .in('id', productIds);

        if (products) {
          productsMap = new Map(products.map(p => [p.id, p]));
        }
      }

      // Transform sub_orders to include product data
      const transformedSubOrders = (subOrders || []).map(subOrder => {
        const product = productsMap.get(subOrder.product_id);
        return {
          ...subOrder,
          products: {
            name: subOrder.product_name || 'מוצר לא זמין',
            description: subOrder.product_description || '',
            measurement_unit: product?.measurement_unit || 'יח\'',
            package_coverage_per_unit: product?.package_coverage_per_unit,
            items_per_package: product?.items_per_package,
            images: product?.images || [],
            main_image_index: product?.main_image_index || 0
          }
        };
      });

      setOrderDetails({
        ...order,
        profiles: profile,
        sub_orders: transformedSubOrders
      });
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error("שגיאה", {
        description: "לא ניתן לטעון את פרטי ההזמנה",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (newStatus: OrderStatus) => {
    if (!orderDetails) return;

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus as any })
        .eq('id', orderDetails.id);

      if (error) throw error;

      // Send email notification for relevant status changes
      if (['processing', 'ready_for_pickup', 'shipped', 'completed', 'cancelled'].includes(newStatus)) {
        try {
          await supabase.functions.invoke('send-order-status-email', {
            body: {
              orderId: orderDetails.id,
              newStatus: newStatus,
              orderNumber: orderDetails.order_number || orderDetails.id.slice(-6)
            }
          });
          console.log('Status email sent successfully');
        } catch (emailError) {
          console.error('Failed to send status email:', emailError);
          // Don't fail the status update if email fails
        }
      }

      toast.success("הצלחה", {
        description: `סטטוס ההזמנה עודכן ל${getStatusLabel(newStatus)}`
      });

      fetchOrderDetails();
      onOrderUpdated();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error("שגיאה", {
        description: "לא ניתן לעדכן את סטטוס ההזמנה",
      });
    }
  };

  const resyncPaymentStatus = async () => {
    if (!orderDetails) return;

    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('reconcile-payment-status', {
        body: { orderId: orderDetails.id }
      });

      if (error) throw error;

      toast.success("הצלחה", {
        description: data?.message || "סטטוס התשלום סונכרן בהצלחה"
      });

      fetchOrderDetails();
      onOrderUpdated();
    } catch (error: any) {
      console.error('Error resyncing payment status:', error);
      toast.error("שגיאה", {
        description: error?.message || "לא ניתן לסנכרן את סטטוס התשלום",
      });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'approved': return 'default';
      case 'in_progress': return 'outline';
      case 'completed': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: 'ממתין לתשלום', color: '#6B7280' },
      processing: { label: 'בטיפול', color: '#22C55E' },
      ready_for_pickup: { label: 'מוכן לאיסוף', color: '#F59E0B' },
      shipped: { label: 'נשלח', color: '#0EA5E9' },
      cancelled: { label: 'בוטל', color: '#DC2626' },
      completed: { label: 'הושלם', color: '#059669' },
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, color: '#6B7280' };
    return (
      <Badge
        variant="secondary"
        style={{
          backgroundColor: statusInfo.color,
          color: 'white',
          border: 'none'
        }}
      >
        {statusInfo.label}
      </Badge>
    );
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'ממתין לתשלום';
      case 'processing': return 'בטיפול';
      case 'ready_for_pickup': return 'מוכן לאיסוף';
      case 'shipped': return 'נשלח';
      case 'cancelled': return 'בוטל';
      case 'completed': return 'הושלם';
      default: return status;
    }
  };

  if (!orderDetails) return null;

  /* Email Logs Rendering helper can be removed if not used or moved */
  // It seems I intended to use it but implemented EmailLogList component instead separately.
  // I will just remove this function definition and the extra return to fix the syntax.

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-xl font-bold">
              פרטי הזמנה #{orderDetails.order_number || orderDetails.id.slice(-6)}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditDialogOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              ערוך הזמנה
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  פרטי הלקוח
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {orderDetails.profiles?.first_name || orderDetails.profiles?.last_name
                      ? `${orderDetails.profiles.first_name || ''} ${orderDetails.profiles.last_name || ''}`
                      : (orderDetails.billing_address?.firstName || orderDetails.billing_address?.lastName || orderDetails.billing_address?.first_name || orderDetails.billing_address?.last_name)
                        ? `${orderDetails.billing_address?.firstName || orderDetails.billing_address?.first_name || ''} ${orderDetails.billing_address?.lastName || orderDetails.billing_address?.last_name || ''}`
                        : 'לא הוזן שם'
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{orderDetails.profiles?.email || orderDetails.billing_address?.email || 'לא הוזן אימייל'}</span>
                </div>
                {(orderDetails.profiles?.phone || orderDetails.billing_address?.phone) && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{orderDetails.profiles?.phone || orderDetails.billing_address?.phone}</span>
                  </div>
                )}
                {(orderDetails.profiles?.business_name || orderDetails.billing_address?.businessName || orderDetails.billing_address?.business_name) && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>שם עסק: {orderDetails.profiles?.business_name || orderDetails.billing_address?.businessName || orderDetails.billing_address?.business_name}</span>
                  </div>
                )}
                {(orderDetails.profiles?.tax_id || orderDetails.billing_address?.taxId || orderDetails.billing_address?.tax_id) && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>ח.פ: {orderDetails.profiles?.tax_id || orderDetails.billing_address?.taxId || orderDetails.billing_address?.tax_id}</span>
                  </div>
                )}
                {(orderDetails.profiles?.business_address || orderDetails.billing_address?.businessAddress || orderDetails.billing_address?.business_address) && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>כתובת עסק: {orderDetails.profiles?.business_address || orderDetails.billing_address?.businessAddress || orderDetails.billing_address?.business_address}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>תאריך הזמנה: {new Date(orderDetails.created_at).toLocaleDateString('he-IL')} בשעה {new Date(orderDetails.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </CardContent>
            </Card>

            {/* Delivery Method - Show at top */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  אופן אספקה
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-lg font-semibold">
                  {orderDetails.sub_orders.every(sub => sub.delivery_option === 'pickup_only')
                    ? 'איסוף עצמי'
                    : orderDetails.sub_orders.every(sub => sub.delivery_option === 'delivery_only')
                      ? 'משלוח'
                      : 'משולב (איסוף ומשלוח)'}
                </div>
                {orderDetails.sub_orders.some(sub => sub.delivery_option !== 'pickup_only') && (
                  <>
                    {orderDetails.sub_orders
                      .filter(sub => sub.delivery_option !== 'pickup_only')
                      .some(sub => sub.delivery_address) ? (
                      <div className="bg-muted p-3 rounded-md space-y-1">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">כתובת למשלוח:</span>
                        </div>
                        {orderDetails.sub_orders
                          .filter(sub => sub.delivery_option !== 'pickup_only' && sub.delivery_address)
                          .map((sub, idx) => (
                            <div key={idx} className="text-sm pr-6">{sub.delivery_address}</div>
                          ))}
                      </div>
                    ) : orderDetails.shipping_city ? (
                      <div className="bg-muted p-3 rounded-md flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">עיר משלוח: {orderDetails.shipping_city}</span>
                      </div>
                    ) : (
                      <div className="bg-amber-50 p-3 rounded-md">
                        <span className="text-sm text-amber-600">כתובת למשלוח לא צוינה</span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle>סיכום הזמנה</CardTitle>
                  <div className="space-y-2">
                    <Badge variant={getStatusVariant(orderDetails.status as OrderStatus)}>
                      {getStatusLabel(orderDetails.status as OrderStatus)}
                    </Badge>
                    <div className="flex gap-2">
                      <Select value={orderDetails.status} onValueChange={updateOrderStatus}>
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">ממתין לתשלום</SelectItem>
                          <SelectItem value="processing">בטיפול</SelectItem>
                          {/* Show ready_for_pickup only for pickup orders */}
                          {orderDetails.sub_orders.some(sub => sub.delivery_option === 'pickup_only') && (
                            <SelectItem value="ready_for_pickup">מוכן לאיסוף</SelectItem>
                          )}
                          {/* Show shipped only for delivery orders */}
                          {orderDetails.sub_orders.some(sub => sub.delivery_option === 'delivery_only' || sub.delivery_option === 'both') && (
                            <SelectItem value="shipped">נשלח</SelectItem>
                          )}
                          <SelectItem value="completed">הושלם</SelectItem>
                          <SelectItem value="cancelled">בוטל</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resyncPaymentStatus}
                        disabled={syncing}
                        title="סנכרן מחדש את סטטוס התשלום עם מערכת התשלומים"
                      >
                        {syncing ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            סנכרן תשלום
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">סכום כולל:</span>
                    <div className="text-lg font-semibold">₪{orderDetails.total_amount.toLocaleString()}</div>
                  </div>
                  {orderDetails.sub_orders.some(sub => sub.delivery_option !== 'pickup_only') && (
                    <div>
                      <span className="text-sm text-muted-foreground">עלות משלוח כוללת:</span>
                      <div className="text-lg font-semibold">₪{orderDetails.total_delivery_cost.toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sub Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  פריטי ההזמנה ({orderDetails.sub_orders.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {orderDetails.sub_orders.map((subOrder, index) => (
                  <div key={subOrder.id} className="border-b last:border-b-0 pb-3 last:pb-0">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 bg-muted rounded-md overflow-hidden flex-shrink-0">
                        {subOrder.products?.images && subOrder.products.images.length > 0 ? (
                          <img
                            src={subOrder.products.images[subOrder.products.main_image_index || 0]}
                            alt={subOrder.products.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted">
                            <Package className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 space-y-2">
                        <div>
                          <h4 className="font-medium">{subOrder.products.name}</h4>
                          {subOrder.products.description && (
                            <p className="text-sm text-muted-foreground">{subOrder.products.description}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">כמות:</span>

                            <div>
                              {['package_by_square_meter', 'package_by_meter'].includes(subOrder.products.measurement_unit) ? (
                                <div className="flex flex-col">
                                  <span>{subOrder.quantity} חבילות</span>
                                  <span className="text-xs text-muted-foreground" dir="ltr">
                                    {subOrder.products.package_coverage_per_unit
                                      ? `(Total ${(subOrder.quantity * subOrder.products.package_coverage_per_unit).toLocaleString(undefined, { maximumFractionDigits: 2 })} sqm)`
                                      : ''}
                                  </span>
                                </div>
                              ) : (
                                <span>{subOrder.quantity} {subOrder.products.measurement_unit}</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">מחיר ליח':</span>
                            <div>₪{subOrder.unit_price.toLocaleString()}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">סה"כ מוצר:</span>
                            <div>₪{subOrder.total_price.toLocaleString()}</div>
                          </div>
                        </div>

                        <div className="bg-muted/50 p-3 rounded-lg">
                          <div className="flex justify-between items-center font-semibold">
                            <span>סה"כ פריט:</span>
                            <span className="text-lime">
                              ₪{subOrder.total_price.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {subOrder.notes && (
                          <div>
                            <span className="text-sm font-medium">הערות:</span>
                            <div className="text-sm text-muted-foreground">{subOrder.notes}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Email Logs Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  לוג אימיילים
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EmailLogList orderId={orderId} />
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>

      <OrderEditDialog
        orderId={orderId}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onOrderUpdated={() => {
          fetchOrderDetails();
          onOrderUpdated();
        }}
      />
    </Dialog>
  );
};

// Simple internal component for email logs
const EmailLogList = ({ orderId }: { orderId: string }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const { data, error } = await supabase
          .from('email_logs' as any) // Cast to any since table might not be in types yet
          .select('*')
          .eq('order_id', orderId)
          .order('created_at', { ascending: false });

        if (!error && data) {
          setLogs(data);
        }
      } catch (e) {
        console.log('Email logs table likely does not exist yet or empty');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [orderId]);

  if (loading) return <div className="text-sm text-muted-foreground">טוען לוגים...</div>;
  if (logs.length === 0) return <div className="text-sm text-muted-foreground">לא נמצאו רישומים שנשלחו אימיילים.</div>;

  return (
    <div className="space-y-4">
      {logs.map((log) => (
        <div key={log.id} className={`p-3 rounded border ${log.status === 'failed' ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
          <div className="flex justify-between mb-1">
            <span className="font-semibold text-sm">{log.subject || log.email_type}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(log.created_at).toLocaleString('he-IL')}
            </span>
          </div>
          <div className="text-xs text-muted-foreground flex gap-2">
            <span>אל: {log.recipient_email}</span>
            <span className={log.status === 'sent' ? 'text-green-600' : 'text-red-600'}>
              ({log.status === 'sent' ? 'נשלח' : 'נכשל'})
            </span>
          </div>
          {log.error_message && (
            <div className="text-xs text-red-600 mt-1">שגיאה: {log.error_message}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default OrderDetailModal;